// Delete an item (meeting / note / task) and optionally the tasks created from its notes.
import { useEffect, useState } from "react";
import { api, errorMessage } from "../api";
import type { Item } from "../api/types";
import { connectorName } from "../lib/archive";
import { deleteItem, deleteItemWithCreated, loadConnectors, toast, toastError } from "../store/app";
import { useAllCollections, useApp } from "../store/hooks";
import { DueChip, Modal, Spinner, SyncBadges } from "./bits";

const KIND_NOUN: Record<Item["type"], string> = { meeting: "meeting", note: "note", task: "task" };

export function DeleteItemDialog({
  item,
  onClose,
  onDeleted,
}: {
  item: Item;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [created, setCreated] = useState<Item[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const collections = useAllCollections();
  const manifests = useApp((s) => s.connectors);
  const noun = KIND_NOUN[item.type];

  useEffect(() => {
    void loadConnectors();
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .createdItems(item.id)
      .then((r) => !cancelled && setCreated(r.items))
      .catch((e) => {
        if (cancelled) return;
        setLoadError(errorMessage(e));
        setCreated([]);
      });
    return () => {
      cancelled = true;
    };
  }, [item.id]);

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const syncedSelected = (created ?? []).filter((c) => selected.has(c.id) && c.sync.length > 0);
  const tools = [...new Set(syncedSelected.flatMap((c) => c.sync.map((s) => connectorName(s.connector_type, manifests))))];

  return (
    <Modal title={`Delete ${noun} “${item.title || "(untitled)"}”?`} onClose={onClose} wide={!!created?.length}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            if (created && created.length === 0 && loadError) await deleteItem(item.id);
            else await deleteItemWithCreated(item.id, [...selected]);
            const n = selected.size;
            toast(
              n ? `Deleted ${noun} and ${n} task${n === 1 ? "" : "s"}` : `Deleted ${noun}`,
              "success",
            );
            onClose();
            onDeleted();
          } catch (err) {
            toastError(err, "Delete failed");
            setBusy(false);
          }
        }}
      >
        {created === null ? (
          <Spinner label="Looking for tasks created from it…" />
        ) : created.length === 0 ? (
          <p>The {noun} and its notes are removed. No tasks were created from it.</p>
        ) : (
          <>
            <p>
              {created.length} task{created.length === 1 ? " was" : "s were"} created from this {noun}. By default they are{" "}
              <strong>kept</strong> (only the link to the deleted notes is removed). Tick the ones to delete too.
            </p>
            <div className="select-bar">
              <button type="button" className="btn-link small" onClick={() => setSelected(new Set(created.map((c) => c.id)))}>
                Select all
              </button>
              <button type="button" className="btn-link small" onClick={() => setSelected(new Set())}>
                Select none
              </button>
              <span className="muted small">{selected.size} selected to delete</span>
            </div>
            <ul className="created-list">
              {created.map((c) => (
                <li key={c.id}>
                  <label className="check-row">
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggle(c.id)}
                      aria-label={`Also delete ${c.title}`}
                    />
                    <span className="created-main">
                      <span className={c.status_category === "done" ? "strike" : ""}>{c.title || "(untitled)"}</span>
                      <span className="created-meta">
                        <span className="chip chip-status">{c.status_category.replace("_", " ")}</span>
                        <span className="muted small">{collections.find((x) => x.id === c.collection_id)?.name}</span>
                        <DueChip item={c} />
                        <SyncBadges sync={c.sync} />
                      </span>
                    </span>
                    <span className="small muted keep-flag">{selected.has(c.id) ? "Also delete" : "Keep"}</span>
                  </label>
                </li>
              ))}
            </ul>
            {syncedSelected.length > 0 && (
              <p className="warn-note" role="alert">
                {syncedSelected.length} selected task{syncedSelected.length === 1 ? " is" : "s are"} synced — deleting{" "}
                {syncedSelected.length === 1 ? "it" : "them"} also deletes {syncedSelected.length === 1 ? "it" : "them"} in{" "}
                {tools.join(" and ")}.
              </p>
            )}
          </>
        )}
        {loadError && <p className="muted small">Could not list created tasks ({loadError}).</p>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn danger-solid" disabled={busy || created === null}>
            {busy
              ? "Deleting…"
              : selected.size
                ? `Delete ${noun} + ${selected.size} task${selected.size === 1 ? "" : "s"}`
                : `Delete ${noun}`}
          </button>
        </div>
      </form>
    </Modal>
  );
}
